import axios from "axios";
import type { PaymentMethod } from "@/interfaces/paymentMethod";
import { apiClient } from "@/lib/apiClient";

/** Rethrows the server's error message (not a bare axios error) so the UI can show it. */
function rethrowApiError(error: unknown, fallback: string): never {
  if (axios.isAxiosError(error) && error.response?.data?.error) {
    throw new Error(error.response.data.error as string);
  }
  throw error instanceof Error ? error : new Error(fallback);
}

/** A group's saved cards, from our display cache (server-side). */
export async function fetchPaymentMethods(groupId: number): Promise<PaymentMethod[]> {
  const { data } = await apiClient.get<{ success: boolean; data: PaymentMethod[] }>(
    `/admin/groups/${groupId}/payment-methods`,
  );
  return data.data;
}

/**
 * Mints a SetupIntent and returns its client_secret — a single-use capability the Payment
 * Element uses to confirm one card. Safe for the browser; never logged or stored.
 */
export async function createSetupIntent(groupId: number): Promise<string> {
  try {
    const { data } = await apiClient.post<{ success: boolean; clientSecret: string }>(
      `/admin/groups/${groupId}/payment-methods/setup-intent`,
    );
    return data.clientSecret;
  } catch (error) {
    rethrowApiError(error, "Couldn't start adding a card. Please try again.");
  }
}

/** Makes a card the group's default (Stripe first, then our cache). */
export async function setDefaultPaymentMethod(
  groupId: number,
  paymentMethodId: number,
): Promise<void> {
  try {
    await apiClient.post(`/admin/groups/${groupId}/payment-methods/${paymentMethodId}/default`);
  } catch (error) {
    rethrowApiError(error, "Couldn't set the default card. Please try again.");
  }
}

/** Detaches the card in Stripe and soft-deletes our row. */
export async function removePaymentMethod(groupId: number, paymentMethodId: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/groups/${groupId}/payment-methods/${paymentMethodId}`);
  } catch (error) {
    rethrowApiError(error, "Couldn't remove the card. Please try again.");
  }
}
